import { Chart } from "chart.js/auto";
import { LitElement, PropertyValues, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";

function formatDate(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

// Function to encode a date for URL
function encodeDate(date) {
    return encodeURIComponent(formatDate(date));
}

// Map of date range strings to functions that calculate the start and end dates
const dateRangeMap = {
    "0-14 days ago": () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 14);
        return { startDate, endDate };
    },
    "15-30 days ago": () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        return { startDate, endDate };
    },
    "> 30 days ago": () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setFullYear(startDate.getFullYear() - 1); // Approximate, adjust as needed
        return { startDate, endDate };
    },
};

// Function to convert a date range string to a URL query string
function convertToUrlQuery(input) {
    const rangeFunction = dateRangeMap[input];
    if (!rangeFunction) {
        throw new Error(`Unsupported date range: ${input}`);
    }

    const { startDate, endDate } = rangeFunction();
    const encodedStartDate = encodeDate(startDate);
    const encodedEndDate = encodeDate(endDate);

    return `created__range__gte=${encodedStartDate}&created__range__lte=${encodedEndDate}`;
}

const chartConfig = {
    1: {
        type: "bar",
        data: {
            labels: [
                "QAs picked up",
                "QAs waiting for UBA",
                "QAs ready to pay out",
                "QAs completed",
                "QAs manually flagged",
            ],
            datasets: [
                {
                    label: "0-14 days ago",
                    data: [5423, 1021, 422, 6532, 477],
                    fill: false,
                    borderWidth: 1,
                    backgroundColor: "rgba(132, 44, 255, 0.7)",
                },
                {
                    label: "15-30 days ago",
                    data: [2420, 624, 322, 3852, 645],
                    fill: false,
                    borderWidth: 1,
                    backgroundColor: "rgba(65, 166, 97, 0.7)",
                },
                {
                    label: "> 30 days ago",
                    data: [123, 42, 22, 352, 45],
                    fill: false,
                    borderWidth: 1,
                    backgroundColor: "rgba(255, 99, 132, 0.7)",
                },
            ],
        },
        options: {
            plugins: {
                title: {
                    display: true,
                    text: "Current QA Statuses",
                },
            },
            onClick: function (event, elements) {
                if (elements.length > 0) {
                    const index = elements[0].index;
                    const datasetIndex = elements[0].datasetIndex;
                    const datasetLabel = this.data.datasets[datasetIndex].label;

                    if (!(datasetLabel in dateRangeMap)) {
                        return;
                    }

                    const urlQuery = convertToUrlQuery(datasetLabel);
                    // should also make a map for the label to status
                    window.open(
                        `https://backoffice.thg-quoten.de/admin/tqs/quotaapplication/?status__exact=13&${urlQuery}`
                    );
                }
            },
        },
    },
    2: {
        type: "bar",
        data: {
            labels: [
                "QAs picked up",
                "QAs waiting for UBA",
                "QAs ready to pay out",
                "QAs completed",
                "QAs manually flagged",
            ],
            datasets: [
                {
                    label: "0-14 days ago",
                    data: [1631, 500, 200, 2532, 177],
                    fill: false,
                    borderWidth: 1,
                    backgroundColor: "rgba(132, 44, 255, 0.7)",
                },
                {
                    label: "15-30 days ago",
                    data: [5423, 1021, 422, 6532, 477],
                    fill: false,
                    borderWidth: 1,
                    backgroundColor: "rgba(65, 166, 97, 0.7)",
                },
                {
                    label: "> 30 days ago",
                    data: [2420, 624, 322, 3852, 645],
                    fill: false,
                    borderWidth: 1,
                    backgroundColor: "rgba(255, 99, 132, 0.7)",
                },
            ],
        },
        options: {
            plugins: {
                title: {
                    display: true,
                    text: "Current QA Statuses",
                },
            },
            onClick: function (event, elements) {
                if (elements.length > 0) {
                    const index = elements[0].index;
                    const datasetIndex = elements[0].datasetIndex;
                    const datasetLabel = this.data.datasets[datasetIndex].label;

                    if (!(datasetLabel in dateRangeMap)) {
                        return;
                    }

                    // should also make a map for the label to status
                    const urlQuery = convertToUrlQuery(datasetLabel);
                    window.open(
                        `https://backoffice.thg-quoten.de/admin/tqs/quotaapplication/?status__exact=13&${urlQuery}`
                    );
                }
            },
        },
    },
    3: {},
};

@customElement("c-qa-status")
export class QaStatusWidget extends LitElement {
    static styles = css`
        :host {
            display: block;
            height: 100%;
        }

        select {
            position: absolute;
            right: 0;
        }
    `;

    canvas: Ref<HTMLCanvasElement> = createRef();

    async firstUpdated() {
        this.loadChart(this.selected);
    }

    willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has("selected")) {
            this.loadChart(this.selected);
        }
    }

    currentChart = null;

    loadChart(selected: string) {
        if (this.currentChart) {
            this.currentChart.destroy();
        }

        this.currentChart = new Chart(this.canvas.value, chartConfig[selected]);
    }

    @state()
    selected = "1";

    render() {
        return html`
            <select
                @change=${(e) => {
                    this.selected = e.target.value;
                }}
            >
                <option value="1">B2B</option>
                <option value="2">B2C</option>
            </select>
            <canvas ${ref(this.canvas)}></canvas>
        `;
    }
}
